import React, { useEffect } from 'react';
import block from 'bem-cn';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import { actions as authAction } from 'features/Auth';
import { actions as userSettingsAction } from 'features/userSettings';
import { actions as rankingActions } from 'features/ranking';
import { actions as agentFeedbackActions } from 'features/AgentFeedback';

import Notify from 'features/notify/view/desktop';
import { ModalProvider } from 'components/ModalProvider/ModalProvider';
import Users from 'features/users/desktop';
import Statistic from 'features/statistic/view/desktop';

import General from 'features/reports/view/desktop/Pages/FinancialReports/General';
import Cash from 'features/reports/view/desktop/Pages/FinancialReports/Cash';
import DetailByPlayer from 'features/reports/view/desktop/Pages/PlayerReports/DetailByPlayer';
import PlayerHistory from 'features/reports/view/desktop/Pages/PlayerReports/PlayerHistory';
import { MyDebtAccount, Payments, HowToStart, FlatPage } from 'features/finance/desktop';
import { actions as reportActions } from 'features/reports/redux';
import convertPlayersDeposits from 'features/reports/model/playerDeposits/convertPlayersDeposits';
import { initialValues } from 'features/reports/model/playerDeposits/usePlayersDeposits';

import { Tree } from 'features/tree/desktop';

import AsyncPollNotifications from 'components/AsyncPoll/AsyncPollNotifications';
import Header from 'components/Header/desktop';
import Subheader from 'components/Subheader/desktop';
import Sidebar from 'components/Sidebar/desktop';
import { getTitlePageFromRoute } from 'components/Subheader/data';
import CreatePlayer from 'features/users/view/desktop/Modals/CreatePlayer/CreatePlayer';
import CreateAgent from 'features/users/view/desktop/Modals/CreateAgent/CreateAgent';
import Deposit from 'features/users/view/desktop/Modals/Deposit/Deposit';
import Withdrawal from 'features/users/view/desktop/Modals/Withdrawal/Withdrawal';
import ChangePassword from 'features/users/view/desktop/Modals/ChangePassword/ChangePassword';
import DisableUser from 'features/users/view/desktop/Modals/DisableUser/DisableUser';
import ModifyAgent from 'features/users/view/desktop/Modals/ModifyAgent/ModifyAgent';
import Information from 'features/users/view/desktop/Modals/Information/Information';
import ChangePermissions from 'features/users/view/desktop/Modals/ChangePermissions/ChangePermissions';
import SportHistory from 'features/sportHistory/view/desktop/SportHistory';
import Custom from 'features/creativeFunctionality/view/desktop/Pages/Custom';
import CustomRequests from 'features/creativeFunctionality/view/desktop/Pages/CustomRequests';
import RequiredChangePasswordModal from 'features/users/view/desktop/Modals/RequiredChangePassword/RequiredChangePasswordModal ';
import Ranking from 'features/ranking/view/desktop';
import Domains from 'features/domains/view/desktop';
import DomainFeaturePopup from 'features/domains/view/DomainFeaturePopup';
import SurveyModal from 'features/survey/view/SurveyModal/SurveyModal';
import PragmaticTournament from 'features/ranking/view/desktop/PragmaticTournament';
import PlayerDeposits from 'features/reports/view/desktop/Pages/PlayerReports/PlayerDeposits';
import RequestsHistory from 'features/reports/view/desktop/Pages/FinancialReports/RequestHistory';
import Banner from './Banner';

import Auth from 'features/Auth/desktop';

// import JacktopTournament from 'features/bonusProgram/view/desktop/JacktopTournament';
// import RubyPlayTournament from 'features/bonusProgram/view/desktop/RubyPlayTournament';
import NetentTournament from 'features/bonusProgram/view/desktop/NetentTournament';

import BonusProgramModule from 'modules/BonusProgram/desktop';
import CashbackModule from 'modules/Cashback/desktop';
import Assistant, { AssistantButton } from 'features/assistant/desktop';

import './App.scss';


const b = block('app');

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isOpened } = useSelector(state => state.assistant, shallowEqual);
  const { isAuth, role, canCreateOnlyPlayer, isPasswordChanged, isPasswordDifficult } = useSelector(
    state => state.auth,
    shallowEqual,
  );
  const semiAutoWithdrawalEnabled = useSelector(state => state.auth.semiAutoWithdrawalEnabled);

  useEffect(() => {
    dispatch(authAction.checkAuth());
  }, [dispatch]);

  useEffect(() => {
    dispatch(userSettingsAction.getSettings());
    if (isAuth) {
      dispatch(rankingActions.getAgentsRanking());
      dispatch(agentFeedbackActions.getFeedbackLink());
    }
  }, [isAuth, dispatch]);

  useEffect(() => {
    if (isAuth && semiAutoWithdrawalEnabled) {
      dispatch(reportActions.getPlayerDeposits(convertPlayersDeposits(initialValues)));
    }
  }, [isAuth, semiAutoWithdrawalEnabled, dispatch]);

  const getTitle = () => {
    return getTitlePageFromRoute.find(el => location.pathname.includes(el.route));
  };
  const title = getTitle();

  const isAssistantButtonShown = isAuth && !isOpened;
  const shouldWeShowRequiredPasswordChangeModal =
    isAuth && (isPasswordChanged === false || isPasswordDifficult === false);

  return (
    <ModalProvider>
      <div className={b()}>
        <Notify />
        <AsyncPollNotifications />
        {shouldWeShowRequiredPasswordChangeModal && <RequiredChangePasswordModal canClose={false} />}

        {isOpened && <Assistant />}

        {isAuth ? (
          <>
            <Sidebar />
            <DomainFeaturePopup />
            <SurveyModal />
            <div className={b('wrapper')}>
              <main className={b('wrapper', 'main')}>
                <div className={b('header')}>
                  <Header />
                </div>
                <Banner />
                {!title?.userPage && !title?.myStatistic && <Subheader />}

                <div className={b('wrapper', 'content')}>
                  <Switch>
                    {canCreateOnlyPlayer && <Redirect exact from="/user/create-agent" to="/users/all" />}
                    {canCreateOnlyPlayer && <Redirect exact from="/users/agents" to="/users/all" />}
                    <Route exact key="/statistic" path="/statistic" component={Statistic} />
                    <Route exact key="/users" path="/users" component={Users} />
                    <Route key="/users/:tab" path="/users/:tab" component={Users} />
                    <Route exact key="/user/create-player" path="/user/create-player" component={CreatePlayer} />
                    <Route exact key="/user/create-agent" path="/user/create-agent" component={CreateAgent} />
                    <Route exact key="/user/deposit" path="/user/deposit/:userId" component={Deposit} />
                    <Route exact key="/user/withdrawal" path="/user/withdrawal/:userId" component={Withdrawal} />
                    <Route exact key="/user/enable/:userId" path="/user/enable/:userId" component={DisableUser} />
                    <Route
                      exact
                      key="/user/change-password/:userId"
                      path="/user/change-password/:userId"
                      component={ChangePassword}
                    />
                    <Route
                      exact
                      key="/user/permissionsDetails/:userId"
                      path="/user/permissionsDetails/:userId"
                      component={ChangePermissions}
                    />
                    <Route
                      exact
                      key="/creative-functionality/custom"
                      path="/creative-functionality/custom"
                      component={Custom}
                    />
                    <Route
                      exact
                      key="/creative-functionality/custom-requests"
                      path="/creative-functionality/custom-requests"
                      component={CustomRequests}
                    />

                    <Route
                      exact
                      key="/user/disable/:userId"
                      path="/user/disable/:userId"
                      render={() => <DisableUser isDisable />}
                    />
                    <Route exact key="/user/edit/:userId" path="/user/edit/:userId" component={ModifyAgent} />
                    <Route exact key="/user/info/:userId" path="/user/info/:userId" component={Information} />
                    <Route
                      exact
                      key="/reports/financial-reports/general"
                      path="/reports/financial-reports/general"
                      component={General}
                    />
                    <Route
                      exact
                      key="/reports/financial-reports/cash"
                      path="/reports/financial-reports/cash"
                      component={Cash}
                    />
                    {canCreateOnlyPlayer && (
                      <Route
                        exact
                        key="/reports/player-reports/detail-by-player"
                        path="/reports/player-reports/detail-by-player"
                        component={DetailByPlayer}
                      />
                    )}
                    <Route
                      exact
                      key="/reports/player-reports/player-history"
                      path="/reports/player-reports/player-history"
                      component={PlayerHistory}
                    />
                    <Route
                      exact
                      key="/reports/player-reports/sport-bets"
                      path="/reports/player-reports/sport-bets"
                      component={SportHistory}
                    />
                    <Route
                      exact
                      key="/reports/financial-reports/player-deposits"
                      path="/reports/financial-reports/player-deposits"
                      component={PlayerDeposits}
                    />
                    <Route
                      exact
                      key="/reports/financial-reports/request-history"
                      path="/reports/financial-reports/request-history"
                      component={RequestsHistory}
                    />
                    <Route
                      exact
                      key="/finance/my-debt-account"
                      path="/finance/my-debt-account"
                      component={MyDebtAccount}
                    />
                    <Route exact key="/finance/payments" path="/finance/payments" component={Payments} />
                    <Route exact key="/finance/how-to-start" path="/finance/how-to-start" component={HowToStart} />
                    <Route
                      exact
                      key="/finance/how-to-start/flat/:page"
                      path="/finance/how-to-start/flat/:page"
                      component={FlatPage}
                    />
                    <Route exact key="/ranking" path="/ranking">
                      <Ranking />
                    </Route>
                    <Route exact key="/domains" path="/domains">
                      <Domains />
                    </Route>
                    <Route exact key="/pragmatic-tournament" path="/pragmatic-tournament">
                      <PragmaticTournament />
                    </Route>
                    {/* <Route exact key="/jacktop-tournament" path="/jacktop-tournament">
                      <JacktopTournament />
                    </Route> */}
                    {/* <Route exact key="/ruby-play-tournament" path="/ruby-play-tournament">
                      <RubyPlayTournament />
                    </Route> */}
                    {/* <Route exact key="/netent-tournament" path="/netent-tournament">
                      <NetentTournament />
                    </Route> */}
                    {BonusProgramModule}
                    {CashbackModule}

                    <Redirect from="/" to="/users/all" />
                  </Switch>
                  {!title?.userPage && !title?.withoutTree && !canCreateOnlyPlayer && (
                    <div className={b('user-tree')}>
                      <Tree />
                    </div>
                  )}
                </div>
              </main>
            </div>
          </>
        ) : (
          <Switch>
            <Route exact key="/" path="/" component={Auth} />
            <Redirect to="/" />
          </Switch>
        )}
        {isAssistantButtonShown && (
          <div className={b('assistant')}>
            <AssistantButton />
          </div>
        )}
      </div>
    </ModalProvider>
  );
};

export default App;
